import React, { useState, useEffect } from 'react';
import ListView from './listComponent/ListView';
import ListItem from './listComponent/ListItem';
import SearchField from './layout/SearchField';
import Panel from './layout/Panel';
import Spacer from './layout/Spacer';
import Button from './layout/Button';
import ModalManager from './layout/ModalManager';
import { TooltipKeys } from "./tooltips/TooltipKeys";
import Dialog from './layout/Dialog';
import StringProperty from './properties/StringProperty';
import ContextMenu from './menus/ContextMenu';
import useForceUpdate from './reactUtils/useForceUpdate';
import useEventListener from './reactUtils/useEventListener';
import {GroupsManager} from '../classes/GroupsManager';
import SelectGroupAction from '../actions/SelectGroupAction';
import RemoveGroupAction from '../actions/RemoveGroupAction';
import CreateGroupAction from '../actions/CreateGroupAction';
import RemoveFromGroupAction from '../actions/RemoveFromGroupAction';
import { CustomEventTypes, DndItemTypes } from '../globals';
import Engine from '../classes/Engine';
import SceneObjectUtils from '../classes/utilities/SceneObjectUtils';
import TextUtils from '../classes/utilities/TextUtils';

ObjectGroupsView.onCreateGroup = function ()
{
	let newGroupCounter = 1;
	let newGroupName = 'New Group';
	while (GroupsManager.getGroupByName(newGroupName) !== null)
	{
		newGroupName = `New Group (${newGroupCounter++})`;
	}

	new CreateGroupAction(newGroupName);
}

export default function ObjectGroupsView(props)
{
	useEventListener(CustomEventTypes.onGroupListUpdated, () => forceUpdate(), document);
	useEventListener(CustomEventTypes.objectSelectionChanged, handleSelectionChanged, document);
	useEventListener(CustomEventTypes.objectNameChanged, () => forceUpdate(), document);

	const forceUpdate = useForceUpdate();

	const [selectedGroup, setSelectedGroup] = useState<string | undefined>();
	const [searchTerm, setSearchTerm] = useState("");

	let tempGroupName;

	useEffect(() =>
	{
		handleSelectionChanged();
	}, []);

	function handleSelectionChanged()
	{
		const currentSelection = Engine.getInstance().getPickingManager().getCurrentSelection();
		if (selectedGroup !== currentSelection.groupGuid)
		{
			setSelectedGroup(currentSelection.groupGuid);
			forceUpdate();
		}
	}

	function shouldFilterGroup(groupName)
	{
		return !TextUtils.searchString(groupName, searchTerm);
	}

	function renderGroupObjects(group)
	{
		const currentScene = Engine.getInstance().getCurrentScene();
		if(!currentScene) return [];

		const groupObjects = GroupsManager.getGroupObjects(group.guid, currentScene.userData.guid);
		return groupObjects.map((obj, idx) =>
		{
			return (
				<ListItem key={idx}
					nodeId={obj.userData.guid}
					label={obj.name}
					isSelectable={false}
					parentGroup={group.guid}
					objectRef={obj}
					parentDecoratorsMapID={"ObjectGroupsView"}
					dndOverrides={{
						dragEnabled: false,
						dropEnabled: false,
					}}
					tooltip={TooltipKeys.ObjectGroupsEntrySubObject}
				/>
			);
		});
	}

	function renderFilteredGroups()
	{

		const groupList = [...GroupsManager.getGroupList()];
		if (!groupList.length)
			return null;

		return groupList.map(group =>
		{
			if (!shouldFilterGroup(group.name))
			{
				return (
					<ListItem key={group.guid}
						nodeId={group.guid}
						label={group.name}
						tooltip={TooltipKeys.ObjectGroupsEntry}
						parentDecoratorsMapID={"ObjectGroupsView"}
						dndOverrides={{
							dropDefinitions: [
								{
									type: DndItemTypes.SCENE_HIERARCHY_TREE_ITEM,
									callback: onSceneObjectDropped
								},
							],
						}}
					>
						{
							renderGroupObjects(group)
						}
					</ListItem>
				);
			}
		});
	}

	function onSelectionChanged(groupIdSet)
	{
		const group = groupIdSet.values().next().value;
		new SelectGroupAction(group ? group.nodeId : null);
	}

	function onItemDeleted(params)
	{
		if (params.size)
		{
			const group = params.values().next().value;
			new RemoveGroupAction(group.nodeId);
		}
		else if (typeof params === 'string')
		{
			new RemoveGroupAction(params);
		}
	}

	function onObjectRemovedFromGroup(groupGuid, object)
	{
		new RemoveFromGroupAction(groupGuid, [object]);
	}

	function openGroupRenameDialog(groupProps)
	{
		ModalManager.addModal({
			content:
				<Dialog
				  title={"Rename Group"}
				  actions={[
				    {
				      label: "Confirm",
				      onClick: () =>
				      {
				        GroupsManager.renameGroup(groupProps.nodeId, tempGroupName);
				      },
				    },
				    {
				      label: "Cancel",
				    }
				  ]}
				>
				  <div>
				    {"Rename \"" + groupProps.label + "\" to:"}
				  </div>
				  <StringProperty
				    value={tempGroupName}
				    placeholder={groupProps.label}
				    onChangeCallback={(v) =>
				    {
				      tempGroupName = v;
				    }}
				    autoFocus
				  />
				</Dialog>
		})
	}

	function treeItemRightClick(props, e)
	{
		if(props.parentGroup)
		{
			ContextMenu.show(e, [
				{
					renderObject: "Remove From Group",
					id: "remove",
					callback: onObjectRemovedFromGroup,
					callbackParams: [props.parentGroup, props.objectRef]
				},
			], "Object Groups");
		}
		else
		{
			ContextMenu.show(e, [
				{
					renderObject: "Rename",
					id: "rename",
					callback: openGroupRenameDialog,
					callbackParams: [props]
				},
				{
					renderObject: "Delete",
					id: "delete",
					callback: onItemDeleted,
					callbackParams: [props.nodeId]
				},
			], "Object Groups");
		}
	}

	function onSceneObjectDropped(droppedItemsPropSet, dropTargetProps)
	{
		const sceneObjects = [];
		const selectionRoot = Engine.getInstance().getSelectionRoot();

		if(selectionRoot)
		{
			droppedItemsPropSet.forEach(item =>
			{	
				const sceneObject = SceneObjectUtils.findObjectByGuid(selectionRoot, item.nodeId);
				if(sceneObject)
				{
					sceneObjects.push();
				}
			});
		}

		GroupsManager.addToGroup(dropTargetProps.nodeId, sceneObjects);
	}

	return (
		<Panel>
			<SearchField
				data-name={'object-groups-search'}
				value={searchTerm}
				onChangeCallback={setSearchTerm}
			/>
			<Spacer size={{ y: "2px" }} />
			<ListView
				selectedItems={new Set().add(selectedGroup)}
				selectionChangedCallback={onSelectionChanged}
				parentDecoratorsMapID={"ObjectGroupsView"}
				itemDeleteCallback={onItemDeleted}
				contextAction={treeItemRightClick}
				multiSelectEnabled={false}
				useAltColor={true}
				dndDefaults={{
					dragEnabled: true,
					dropEnabled: true,
					dragType: DndItemTypes.OBJECT_GROUP_TREE_ITEM,
				}}
			>
				{
					renderFilteredGroups()
				}
			</ListView>
			<div className={"listWindowAddItemButtonWrapper"}>
				<Button data-name={'new-object-group'}
					category={"ObjectGroups"}
					label={"Create Group"}
					onClick={ObjectGroupsView.onCreateGroup}
				/>
			</div>
		</Panel>
	);
}
